import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card } from '../components/ui/card';
import { Search, Download, Grid3x3, List, Eye, Edit2, MoreHorizontal, Tag, Mail } from 'lucide-react';
import api from '../lib/api';
import useAuthStore from '../lib/auth-store';
import { LeadImportDialog } from '../components/leads/LeadImportDialog';
import { LeadBulkBar, LeadExportDialog, ImportHistoryDialog } from '../components/leads/LeadBulkTools';

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
}

function LeadsPage() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState([]);
  const [dialog, setDialog] = useState(null);
  const role = useAuthStore((s) => s.user?.role);
  const canManage = role === 'super_admin' || role === 'iso';
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    assigned_rep: 'all',
    location: 'all',
    date_added: 'any'
  });

  useEffect(() => {
    loadLeads();
  }, [filters.status]); // Reload when status filter changes

  const loadLeads = async () => {
    try {
      setLoading(true);
      // Build filter params for API
      const filterParams = {};
      if (filters.status !== 'all') {
        filterParams.status = filters.status;
      }
      
      const data = await api.getLeads(filterParams);
      setLeads(data);
    } catch (error) {
      console.error('Failed to load leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      hot: 'bg-orange-100 text-orange-700 border-orange-300',
      warm: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      cold: 'bg-blue-100 text-blue-700 border-blue-300',
      new: 'bg-green-100 text-green-700 border-green-300',
      qualified: 'bg-purple-100 text-purple-700 border-purple-300',
      contacted: 'bg-teal-100 text-teal-700 border-teal-300',
      in_onboarding: 'bg-indigo-100 text-indigo-700 border-indigo-300',
      submitted: 'bg-emerald-100 text-emerald-700 border-emerald-300'
    };
    return colors[status?.toLowerCase()] || colors.new;
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'NA';
  };

  const filteredLeads = leads.filter(lead => {
    if (searchQuery && !lead.business_name?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !lead.contact_name?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">Leads</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage and track all your potential clients in one place
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setDialog('history')} data-testid="import-history-btn">Import history</Button>
          <Button variant="outline" onClick={() => setDialog('import')} data-testid="import-leads-btn">Import leads</Button>
          <Button
            onClick={() => navigate('/leads/new')}
            className="bg-[#3b82f6] hover:bg-[#2563eb]"
            data-testid="add-lead-btn"
          >
            + Add Lead
          </Button>
        </div>
      </div>
      {dialog === 'import' && <LeadImportDialog onClose={() => setDialog(null)} onDone={loadLeads} />}
      {dialog === 'history' && <ImportHistoryDialog onClose={() => setDialog(null)} onChanged={loadLeads} />}
      {dialog === 'export' && <LeadExportDialog status={filters.status} onClose={() => setDialog(null)} />}
      {selected.length > 0 && <LeadBulkBar ids={selected} canAssign={canManage} onDone={() => { setSelected([]); loadLeads(); }} />}

      {/* Filters Bar */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Status Filter */}
          <div className="w-48">
            <Select
              value={filters.status}
              onValueChange={(value) => setFilters({ ...filters, status: value })}
            >
              <SelectTrigger data-testid="filter-status">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="hot">Hot</SelectItem>
                <SelectItem value="warm">Warm</SelectItem>
                <SelectItem value="cold">Cold</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="qualified">Qualified</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Assigned Rep Filter */}
          <div className="w-48">
            <Select
              value={filters.assigned_rep}
              onValueChange={(value) => setFilters({ ...filters, assigned_rep: value })}
            >
              <SelectTrigger data-testid="filter-rep">
                <SelectValue placeholder="All Reps" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Reps</SelectItem>
                <SelectItem value="me">Me</SelectItem>
                <SelectItem value="demo_agent">Demo Agent</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Location Filter */}
          <div className="w-48">
            <Select
              value={filters.location}
              onValueChange={(value) => setFilters({ ...filters, location: value })}
            >
              <SelectTrigger data-testid="filter-location">
                <SelectValue placeholder="All Locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                <SelectItem value="miami">Miami, FL</SelectItem>
                <SelectItem value="orlando">Orlando, FL</SelectItem>
                <SelectItem value="tampa">Tampa, FL</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Date Filter */}
          <div className="w-48">
            <Select
              value={filters.date_added}
              onValueChange={(value) => setFilters({ ...filters, date_added: value })}
            >
              <SelectTrigger data-testid="filter-date">
                <SelectValue placeholder="Any Time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="quarter">This Quarter</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button variant="default" className="bg-[#3b82f6] hover:bg-[#2563eb]">
            Apply Filters
          </Button>
          <Button variant="outline">
            Reset
          </Button>

          {/* Spacer */}
          <div className="flex-1"></div>

          {/* Results count */}
          <div className="text-sm text-muted-foreground">
            {filteredLeads.length} leads found
          </div>

          {/* Per page */}
          <div className="w-32">
            <Select
              defaultValue="25"
              onValueChange={(value) => console.log('Per page:', value)}
            >
              <SelectTrigger data-testid="per-page-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 per page</SelectItem>
                <SelectItem value="25">25 per page</SelectItem>
                <SelectItem value="50">50 per page</SelectItem>
                <SelectItem value="100">100 per page</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* View options */}
          <div className="flex border rounded-lg">
            <button className="p-2 hover:bg-accent rounded-l-lg" onClick={() => setDialog('export')} title="Export leads" data-testid="export-leads-btn">
              <Download className="h-4 w-4" />
            </button>
            <button className="p-2 bg-primary text-primary-foreground">
              <List className="h-4 w-4" />
            </button>
            <button className="p-2 hover:bg-accent rounded-r-lg">
              <Grid3x3 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </Card>

      {/* Leads Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr className="border-b text-left">
                <th className="p-4 w-12">
                  <input type="checkbox" className="w-4 h-4 rounded border-input" data-testid="select-all-checkbox"
                    checked={filteredLeads.length > 0 && selected.length === filteredLeads.length}
                    onChange={(e) => setSelected(e.target.checked ? filteredLeads.map((l) => l.id) : [])} />
                </th>
                <th className="p-4 font-semibold text-sm text-muted-foreground">NAME/BUSINESS</th>
                <th className="p-4 font-semibold text-sm text-muted-foreground">STATUS</th>
                <th className="p-4 font-semibold text-sm text-muted-foreground">ASSIGNED REP</th>
                <th className="p-4 font-semibold text-sm text-muted-foreground">LOCATION</th>
                <th className="p-4 font-semibold text-sm text-muted-foreground">DATE ADDED</th>
                <th className="p-4 font-semibold text-sm text-muted-foreground">LAST CONTACT</th>
                <th className="p-4 font-semibold text-sm text-muted-foreground text-right">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="p-8 text-center text-muted-foreground">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  </td>
                </tr>
              ) : filteredLeads.length === 0 ? (
                <tr>
                  <td colSpan="8" className="p-8 text-center text-muted-foreground">
                    No leads found
                  </td>
                </tr>
              ) : (
                filteredLeads.map((lead) => (
                  <tr 
                    key={lead.id} 
                    className="border-b hover:bg-accent/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/leads/${lead.id}`)}
                    data-testid={`lead-row-${lead.id}`}
                  >
                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" className="w-4 h-4 rounded border-input" checked={selected.includes(lead.id)} onChange={() => toggle(lead.id)} data-testid={`lead-select-${lead.id}`} />
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-[#3b82f6]/10 rounded-full flex items-center justify-center text-sm font-semibold text-[#3b82f6]">
                          {getInitials(lead.contact_name)}
                        </div>
                        <div>
                          <div className="font-medium text-sm">{lead.contact_name}</div>
                          <div className="text-xs text-muted-foreground">{lead.business_name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(lead.status)}`}>
                        {lead.status || 'new'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-7 h-7 bg-purple-100 rounded-full flex items-center justify-center text-xs font-semibold text-purple-600">
                          {getInitials(lead.owner_name)}
                        </div>
                        <span className="text-sm">{lead.owner_name || 'Unassigned'}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-muted-foreground">
                        {lead.city || lead.state ? [lead.city, lead.state].filter(Boolean).join(', ') : '—'}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-muted-foreground">
                        {new Date(lead.created_at).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-muted-foreground">
                        {timeAgo(lead.updated_at || lead.created_at)}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end space-x-1" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => navigate(`/leads/${lead.id}`)}
                          data-testid={`view-lead-${lead.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          data-testid={`edit-lead-${lead.id}`}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          data-testid={`more-lead-${lead.id}`}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && filteredLeads.length > 0 && (() => {
          const PAGE_SIZE = 25;
          const totalPages = Math.max(1, Math.ceil(filteredLeads.length / PAGE_SIZE));
          return (
            <div className="p-4 border-t flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing 1 to {Math.min(PAGE_SIZE, filteredLeads.length)} of {filteredLeads.length} results
              </div>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm" disabled data-testid="leads-page-prev">
                  Previous
                </Button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((p) => (
                  <Button
                    key={p}
                    variant="outline"
                    size="sm"
                    className={p === 1 ? 'bg-[#3b82f6] text-white hover:bg-[#2563eb]' : ''}
                    data-testid={`leads-page-${p}`}
                  >
                    {p}
                  </Button>
                ))}
                <Button variant="outline" size="sm" disabled={totalPages <= 1} data-testid="leads-page-next">
                  Next
                </Button>
              </div>
            </div>
          );
        })()}
      </Card>
    </div>
  );
}

export default LeadsPage;
